<template>
  <transition name="wc-active-order-type-badge">
    <div v-if="shouldShowBadge" class="order-type-bar">
      <div class="order-type-bar__icon">
        <WCSvgIcon name="basket-shopping" icon-size="md" />
      </div>
      <div class="order-type-bar__content">
        <span class="order-type-bar-kicker">{{ $t('youAreShopping') }}</span>
        <span class="order-type-bar-value" :title="displayedOrderType?.name">
          {{ displayedOrderType?.name }}
        </span>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import useOrderTypes from '@/composables/useOrderTypes';
import useEmployee from '@/modules/employee/composables/useEmployee';

const { visibleOrderTypes, currentOrderType: displayedOrderType } = useOrderTypes();
const { isNotAnEmployee } = useEmployee();
const route = useRoute();

const isCarouselVisible = ref(true);
let observer: IntersectionObserver | null = null;

const isMotEnabled = computed(() => (visibleOrderTypes.value?.length ?? 0) > 1);
const isCheckoutRoute = computed(() => route.path === '/co' || route.path.startsWith('/scan/co'));
const isHomePage = computed(() => route.name === 'Home');

const isEligibleForBadge = computed(
  () => Boolean(displayedOrderType.value) && isNotAnEmployee.value && isMotEnabled.value,
);

const shouldShowBadge = computed(() => {
  if (!isEligibleForBadge.value) return false;
  if (isCheckoutRoute.value) return false;
  if (isHomePage.value) return !isCarouselVisible.value;
  return true;
});

onMounted(() => {
  const el = document.querySelector<HTMLElement>('.wc-mobile-ot-switcher');
  if (typeof IntersectionObserver !== 'function' || !el) {
    isCarouselVisible.value = false;
    return;
  }
  observer = new IntersectionObserver(
    ([entry]) => {
      isCarouselVisible.value = entry.isIntersecting;
    },
    { threshold: 0.1 },
  );
  observer.observe(el);
});

onUnmounted(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.order-type-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.4375rem 1.25rem;
  background: var(--primary-lighter-9);
  color: var(--primary, $primary);
}

.order-type-bar :deep(svg),
.order-type-bar :deep(path) {
  fill: currentColor;
}

.order-type-bar__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: $font-size-sm;
}

.order-type-bar__content {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: $font-size-sm;
}

.order-type-bar-kicker {
  font-weight: 400;
  white-space: nowrap;
  opacity: 0.85;
}

.order-type-bar-value {
  font-weight: 700;
  min-width: 0;
  max-width: 62vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wc-active-order-type-badge-enter-active,
.wc-active-order-type-badge-leave-active {
  transition: transform 0.5s ease, opacity 0.25s ease, max-height 0.5s ease;
  max-height: 4rem;
  overflow: hidden;
}

.wc-active-order-type-badge-enter-from,
.wc-active-order-type-badge-leave-to {
  transform: translateY(-100%);
  opacity: 0;
  max-height: 0;
}
</style>
